import React, { Fragment, useCallback, useEffect, useState } from 'react';

// The global `axios` is an instance, which has no isCancel().
import { isCancel } from 'axios';
import Skeleton from 'react-loading-skeleton';

import { formatDateLong } from 'Helpers/dateFormat';
import { milestoneDetailsCache as detailsCache } from 'Helpers/milestoneDetailsCache';

import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextButton from 'CommonComponents/TextButton';

import StarFilledIcon from 'Assets/icons/star-filled.svg';

/**
 * The description and start date are not in the roadmap payload; fetch them while the modal is
 * open, cancelling on close or when another milestone is opened.
 */
const useMilestoneDetails = (detailsUrl, milestoneId, open) => {
    const [state, setState] = useState({ id: null, details: null, error: false });
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => setAttempt((current) => current + 1), []);

    // Read during render, not in an effect, so a cache hit shows its details on the very first
    // paint instead of the Skeleton flashing for one frame first.
    const cached = open && milestoneId ? detailsCache.get(milestoneId) : undefined;

    useEffect(() => {
        if (!open || !milestoneId || !detailsUrl || detailsCache.has(milestoneId)) {
            return undefined;
        }

        const controller = new AbortController();
        setState({ id: milestoneId, details: null, error: false });

        axios
            .get(detailsUrl.replace('ACTIVITY_ID', milestoneId), { signal: controller.signal })
            .then((response) => {
                detailsCache.set(milestoneId, response.data);
                setState({ id: milestoneId, details: response.data, error: false });
            })
            .catch((requestError) => {
                if (isCancel(requestError)) {
                    return;
                }

                setState({ id: milestoneId, details: null, error: true });
            });

        return () => controller.abort();
    }, [detailsUrl, milestoneId, open, attempt]);

    if (cached !== undefined) {
        return { details: cached, error: false, retry };
    }

    const current = state.id === milestoneId;

    return {
        details: current ? state.details : null,
        error: current && state.error,
        retry,
    };
};

// Read-only milestone details, shared by the calendar and list styles. "Depends-on" is omitted:
// ProjectActivity has no dependency relationship in the data model.
const MilestoneDetailModal = ({ modal, milestone, detailsUrl, onClose }) => {
    const isKey = !!milestone?.key;
    const { details, error, retry } = useMilestoneDetails(detailsUrl, milestone?.id, !!modal.visible);

    return (
        <Modal id="project-roadmap-milestone-modal" modal={modal} containerStyle={{ maxWidth: '32rem' }}>
            {milestone && (
                <Fragment>
                    <ModalHeader close={onClose} dark>
                        <span className="tw-inline-flex tw-items-center tw-gap-x-2">
                            {isKey ? (
                                <StarFilledIcon className="tw-h-5 tw-w-5 tw-text-blue tw-shrink-0" />
                            ) : (
                                <span className="tw-h-3.5 tw-w-3.5 tw-rounded-full tw-border-2 tw-border-blue tw-shrink-0" />
                            )}
                            {isKey ? $t('Key milestone') : $t('Milestone')}
                        </span>
                    </ModalHeader>

                    <div className="tw-space-y-4" aria-busy={!details && !error}>
                        <h4 className="tw-text-base tw-font-semibold tw-text-charcoal tw-leading-snug">{milestone.name}</h4>

                        {error ? (
                            <p role="alert" className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-text-sm tw-text-red">
                                {$t('An error occurred, please try again')}
                                <TextButton variant="primary" onClick={retry}>
                                    {$t('Retry')}
                                </TextButton>
                            </p>
                        ) : !details ? (
                            <Skeleton height={14} width="80%" />
                        ) : (
                            details.detailed_description && <p className="tw-text-sm tw-text-charcoal-50 tw-leading-relaxed">{details.detailed_description}</p>
                        )}

                        <table className="tw-w-full tw-text-sm">
                            <tbody>
                                {milestone.owner_name && (
                                    <tr>
                                        <td className="tw-py-1 tw-pe-3 tw-text-charcoal-50 tw-whitespace-nowrap tw-align-top">{$t('Owner')}</td>
                                        <td className="tw-py-1 tw-font-medium tw-text-charcoal">{milestone.owner_name}</td>
                                    </tr>
                                )}
                                {milestone.date && (
                                    <tr>
                                        <td className="tw-py-1 tw-pe-3 tw-text-charcoal-50 tw-whitespace-nowrap tw-align-top">{$t('Due date')}</td>
                                        <td className="tw-py-1 tw-font-medium tw-text-charcoal">{formatDateLong(milestone.date)}</td>
                                    </tr>
                                )}
                                {details?.start_date && (
                                    <tr>
                                        <td className="tw-py-1 tw-pe-3 tw-text-charcoal-50 tw-whitespace-nowrap tw-align-top">{$t('Start date')}</td>
                                        <td className="tw-py-1 tw-font-medium tw-text-charcoal">{formatDateLong(details.start_date)}</td>
                                    </tr>
                                )}
                                {milestone.status_label && (
                                    <tr>
                                        <td className="tw-py-1 tw-pe-3 tw-text-charcoal-50 tw-whitespace-nowrap tw-align-top">{$t('Status')}</td>
                                        <td className="tw-py-1 tw-font-medium tw-text-charcoal">{milestone.status_label}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-pt-4 tw-border-t tw-border-grey">
                            {window.viewProjectActivityDetailsUrl && (
                                <Button
                                    as="a"
                                    variant="primary"
                                    small
                                    href={`${window.viewProjectActivityDetailsUrl.replace('ACTIVITY_ID', milestone.id)}#plan`}
                                >
                                    {$t('Open in Project Plan')}
                                </Button>
                            )}
                            <Button variant="secondary" small className="tw-ms-auto" onClick={onClose}>
                                {$t('Close')}
                            </Button>
                        </div>
                    </div>
                </Fragment>
            )}
        </Modal>
    );
};

export default MilestoneDetailModal;
